import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdMenu, MdVerified, MdOutlineSensors } from "react-icons/md";
import { FaSignOutAlt } from "react-icons/fa";
import { useAuth } from "../context/AuthProvider";

const Header = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const { user, logoutSession } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    if (logoutSession) {
      logoutSession();
    } else {
      localStorage.clear();
    }
    navigate("/login");
  };

  const roleName = user?.role || "User";
  const roleLower = roleName.toLowerCase();
  const isAdmin = roleLower === "admin";
  const isVendor = roleLower === "vendor";

  return (
    <header className="sticky top-0 z-30 w-full bg-white/85 backdrop-blur-md border-b border-[#C7C6D9]/40 shadow-[0_4px_20px_-10px_rgba(11,10,61,0.06)] transition-all">
      <div className="flex items-center justify-between px-4 lg:px-8 py-3">
        {/* Left Side: Mobile Toggle & Brand Tag */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden flex h-9 w-9 items-center justify-center rounded-xl bg-[#0B0A3D]/5 text-[#0B0A3D] transition-colors hover:bg-[#0B0A3D]/10 focus:outline-none"
            aria-label="Toggle Sidebar"
          >
            <MdMenu className="text-xl" />
          </button>

          <Link to="/" className="flex items-center gap-2 group">
            <span className="hidden sm:flex h-2.5 w-2.5 rounded-full bg-[#2F9E68] animate-pulse" />
            <span className="text-xs sm:text-sm font-semibold tracking-tight text-[#6E6E86] group-hover:text-[#0B0A3D] transition-colors">
              OurGenie <span className="hidden md:inline">• {isVendor ? "Vendor Portal" : "Command Center"}</span>
            </span>
          </Link>
        </div>

        {/* Right Side: Operational Status, User Badge & Logout */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Live System Status Indicator */}
          <div className="hidden md:flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2F9E68]/10 border border-[#2F9E68]/20">
            <MdOutlineSensors className="text-[#2F9E68] text-sm animate-pulse" />
            <span className="text-[0.72rem] font-bold text-[#2F9E68] tracking-wider uppercase">
              System Online
            </span>
          </div>

          {/* User Profile Pill */}
          <Link
            to="/"
            className="flex items-center gap-2.5 p-1.5 pl-2.5 pr-3 rounded-xl bg-[#0B0A3D]/5 hover:bg-[#0B0A3D]/10 border border-[#0B0A3D]/10 transition-all duration-200"
          >
            <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${isVendor ? "bg-[#7C3AED] text-white" : "bg-[#0B0A3D] text-[#E4C766]"} font-bold text-xs shadow-sm`}>
              {user?.name ? user.name.charAt(0).toUpperCase() : roleName[0]?.toUpperCase() || "U"}
            </div>

            <div className="flex flex-col text-left">
              <span className="text-xs font-bold text-[#0B0A3D] truncate max-w-[110px] sm:max-w-[150px]">
                {user?.name || roleName}
              </span>
              <div className="flex items-center gap-1">
                <MdVerified className={`text-[10px] ${isAdmin ? "text-[#C9A227]" : isVendor ? "text-[#7C3AED]" : "text-[#2F9E68]"}`} />
                <span className="text-[0.65rem] font-bold uppercase tracking-wider text-[#6E6E86]">
                  {roleName}
                </span>
              </div>
            </div>
          </Link>

          {/* Logout Action Button */}
          <button
            onClick={handleLogout}
            title="Logout of session"
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#D6455A]/10 text-[#D6455A] hover:bg-[#D6455A] hover:text-white transition-all duration-200 focus:outline-none"
          >
            <FaSignOutAlt className="text-sm" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
